import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { formatLabel } from "@/lib/campus";

export const PendingCourses = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [rzp, setRzp] = useState(null);
  const load = () => api.get("/admin/courses/pending").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/payments/razorpay/config").then(({ data }) => setRzp(data)).catch(() => {}); }, []);

  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/courses/${id}/moderate?decision=${decision}`);
      toast.success(decision === "published" ? "Course published" : "Course rejected");
      load();
      onChange?.();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="pending-courses">
      <div className="flex items-center gap-2 text-xs" data-testid="razorpay-status">
        <CreditCard className="h-3.5 w-3.5 text-slate-500" />
        <span className={rzp?.enabled ? "text-emerald-400" : "text-slate-500"}>
          Razorpay (India): {rzp?.enabled ? "connected" : "not connected — add RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET / RAZORPAY_WEBHOOK_SECRET"}
        </span>
      </div>
      <h3 className="text-white font-semibold">Review queue ({items.length})</h3>
      {items.length === 0 && <div className="text-sm text-slate-500" data-testid="pending-empty">Nothing waiting for review.</div>}
      {items.map((c) => (
        <div key={c.course_id} data-testid={`pending-${c.course_id}`} className="rounded-xl bg-[#121721] border border-amber-500/20 p-4">
          <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
            <div className="min-w-0">
              <div className="text-xs text-slate-500">{formatLabel(c.format)} · {c.category} · ${Number(c.price).toFixed(2)} · {c.lesson_count} lessons</div>
              <div className="text-white font-semibold mt-0.5">{c.title}</div>
              <div className="text-xs text-slate-400 mt-1">{c.instructor_name} · <span className="text-emerald-400">{c.contact_email} ✓</span></div>
              <p className="text-sm text-slate-400 mt-2 line-clamp-3">{c.description}</p>
              <div className="flex flex-wrap gap-3 text-xs mt-2">
                <span className="text-slate-300">Value score: <b>{c.review?.value_score ?? "–"}</b></span>
                <span className={c.ai_percentage > 45 ? "text-red-300" : "text-slate-300"}>AI estimate: <b>{c.ai_percentage}%</b>{c.review?.is_ai_subject ? " (AI topic — exempt)" : ""}</span>
                {c.marketable_skill && <span className="text-emerald-300">Marketable skill</span>}
              </div>
              {c.review?.reason && <p className="text-xs text-amber-200/80 mt-1">AI note: {c.review.reason}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`approve-${c.course_id}`} onClick={() => decide(c.course_id, "published")} className="flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-lg"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
              <button data-testid={`reject-${c.course_id}`} onClick={() => decide(c.course_id, "rejected")} className="flex items-center gap-1 text-xs bg-white/5 hover:bg-red-600 text-white px-3 py-2 rounded-lg border border-white/10"><XCircle className="h-3.5 w-3.5" /> Reject</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
